import type { FrameRecords } from "../types/api";
import { fmtMoney, isMoneyKey, type VisibleColumn } from "../lib/columns";
import { rawKeyOf } from "../lib/columnGroups";
import { SORT_LABELS, sortKindOf, trackLabel, type SortSpec } from "../lib/ledgerView";
import HeaderFilter from "./HeaderFilter";
import PinButton from "./PinButton";

// What every grid's column header carries after its label (finance 2026-09-28: the Manual
// Match ledgers' header, on every table). The sort arrow shows only while the column is
// the sort, so the row order is never a guess, and a click reverses it. The filter menu
// leads with Sort worded for the column's kind, lists numbers in numeric order and amounts
// as the grid shows them. Omit `sort` where rows are drawn as match blocks: sorting would
// split a match's legs apart.
export function HeadControls({ frame, col, filter, sort, pin }: {
  frame: FrameRecords;
  col: VisibleColumn;
  filter: {
    active: boolean; open: boolean; options: () => string[]; selected: string[];
    onToggleOpen: () => void; onChange: (v: string[]) => void;
  };
  sort?: { spec: SortSpec | null; onSort: (s: SortSpec) => void };
  pin?: { pinned: boolean; onToggle: () => void };
}) {
  const kind = sortKindOf(frame, col.index, col.key);
  const dir = sort?.spec?.key === col.key ? sort.spec.dir : null;
  const money = kind === "number" && isMoneyKey(rawKeyOf(col.key));
  return (
    <>
      {sort && dir && (
        <button type="button" className="th-sort"
          title={`Sorted ${SORT_LABELS[kind][dir].toLowerCase()}. Click to reverse.`}
          aria-label={`Sorted ${SORT_LABELS[kind][dir].toLowerCase()}. Reverse the order`}
          onClick={() => sort.onSort({ key: col.key, dir: dir === "desc" ? "asc" : "desc" })}>
          {dir === "desc" ? "↓" : "↑"}
        </button>
      )}
      <HeaderFilter
        active={filter.active}
        open={filter.open}
        optionsFor={() => (kind === "number"
          ? [...filter.options()].sort((a, b) => Number(a) - Number(b))
          : filter.options())}
        selected={filter.selected}
        onToggleOpen={filter.onToggleOpen}
        onChange={filter.onChange}
        dateColumn={col.key.toLowerCase().includes("date")}
        sort={sort ? { kind, dir, onSort: (d) => sort.onSort({ key: col.key, dir: d }) } : undefined}
        format={money ? (v) => fmtMoney(Number(v)) : undefined}
      />
      {pin && <PinButton pinned={pin.pinned} onToggle={pin.onToggle} />}
    </>
  );
}

// Track chips: premium settles premium, commission settles commission, so one click takes
// a book down to one track. Each count is what clicking the chip would show. Nothing
// renders for a book with a single track.
export function TrackChips({ tracks, counts, track, onTrack }: {
  tracks: string[];
  counts: Map<string, number>;
  track: string | null;
  onTrack: (t: string | null) => void;
}) {
  if (tracks.length < 2) return null;
  return (
    <div className="ledger-chips" role="group" aria-label="Show one track">
      <button type="button" className="ledger-chip" aria-pressed={track === null}
        onClick={() => onTrack(null)}>
        All<b>{[...counts.values()].reduce((a, b) => a + b, 0).toLocaleString()}</b>
      </button>
      {tracks.map((t) => (
        <button key={t} type="button" className="ledger-chip" aria-pressed={track === t}
          onClick={() => onTrack(track === t ? null : t)}>
          {trackLabel(t)}<b>{(counts.get(t) ?? 0).toLocaleString()}</b>
        </button>
      ))}
    </div>
  );
}
